import locale from '@com/core/common/utils/locale';
import { sanitizeText } from '@com/core/common/utils/string';
import { MARKETPLACE } from '@com/core/common/baseConfig';

const RECENT_SEARCHES_KEY = `recent-searches-${MARKETPLACE}`;
const SHOPPING_URL_KEY = 'shop-url-noon';
const MAX_NUMBER_OF_RECENT_SEARCHES = 10;

/**
 * Creates new cookie or removes cookie with negative expiration
 * @param  {string} key       The key or identifier for the store
 * @param  {string} value     Contents of the store
 * @returns {void}
 */
export const setCookie = (key: string, value: string): void => {
    const expires = `; expires=Fri, 31 Dec 9999 23:59:59 GMT`;
    const hostname = window.location.hostname
        .split('.')
        .slice(-2)
        .join('.');
    // Note: works only when deployed as it's Secure. This is okay since it's meant only for nginx.
    document.cookie = `${key}=${value}${expires}; Secure; Path=/;domain=.${hostname}`;
};

/**
 * Returns contents of cookie
 * @param  {string} key         The key or identifier for the store
 * @param  {string} cookie      Custom cookie string to read
 * @returns {string | null}     Returns a string if exists
 */
export const getCookie = (key: string, customCookie?: string): string | null => {
    const cookie = customCookie || document.cookie;
    if (!cookie) {
        return null;
    }
    const b = document.cookie.match(`(^|[^;]+)\\s*${key}\\s*=\\s*([^;]+)`);
    return (b ? b.pop() : null) || null;
};

/**
 * Returns contents of a local storage item
 * @param  {string} key         The key or identifier for the store
 * @param  {string} value       The value to store
 * @returns {void}
 */
export const saveToLocalStorage = (key: string, value: string): void => {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
    }
};

/**
 * Returns contents of a local storage item
 * @param  {string} key         The key or identifier for the store
 * @returns {string | null}
 */
export const readFromLocalStorage = (key: string): string | null => {
    try {
        const result = localStorage.getItem(key);
        return result;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        return null;
    }
};

// #TODO: Test this, not sure if it does what it needs to
export const copyToClipboard = (text: string, onSuccess: () => void, onError: (error: Error) => void): void => {
    const range = document.createRange(); // CREATES A RANGE AND SELECTION FOR TEXT
    const selection = document.getSelection();
    const elem = document.createElement('span'); // CREATES A DOM ELEMENT & ADD URL AS TEXT
    elem.setAttribute('style', 'position: fixed; height: 0; opacity: 0;'); // MAKE THE ELEMENT INVISIBLE
    elem.textContent = window.location.href;
    try {
        document.body.appendChild(elem); // APPEND ELEMENT TO DOM AND SELECT TEXT CONTENT
        range.selectNode(elem);
        if (selection) {
            selection.addRange(range);
        }

        document.execCommand('copy'); // INIT COPY COMMAND ON SELECTED TEXT
    } catch (error) {
        onError(error);
    } finally {
        if (selection) {
            if (typeof selection.removeRange === 'function') {
                // REMOVE THE SELECTION
                selection.removeRange(range);
            } else {
                selection.removeAllRanges();
            }
        }
        if (elem) {
            document.body.removeChild(elem);
        } // REMOVE THE ELEMENT FROM DOM
        onSuccess();
    }
};

export const saveLocaleAsCookie = (): void => {
    setCookie('nloc', locale.getAPILocale());
};

export const recentSearches = {
    /**
     * Adds keyword to local storage as a recent searched item
     * @function add
     * @param {string} keyword- Keyword to be added
     * @returns {void}
     */
    add: (keyword: string): void => {
        try {
            if (keyword) {
                const sanitizedKeyword = sanitizeText(keyword);
                let searches = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '');
                if (searches && Array.isArray(searches)) {
                    if (searches.indexOf(sanitizedKeyword) === -1) {
                        if (searches.length === MAX_NUMBER_OF_RECENT_SEARCHES) {
                            searches.shift();
                        }
                        searches.push(sanitizedKeyword);
                    }
                } else {
                    searches = [];
                }
                localStorage.setItem(`${RECENT_SEARCHES_KEY}`, JSON.stringify(searches));
            }
        } catch (exception) {
            const searches = [];
            searches.push(keyword);
            localStorage.setItem(`${RECENT_SEARCHES_KEY}`, JSON.stringify(searches));
        }
    },

    /**
     * Fetches recently searched keywords from local storage
     * @function getsearches
     * @returns {string[]} - returns an array of strings
     */
    get: (): string[] | null => {
        if (localStorage.getItem(`${RECENT_SEARCHES_KEY}`)) {
            const searches = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '');
            if (searches && searches.length) {
                return searches.reverse();
            }
        }

        return null;
    },

    /**
     * Removes a keyword or the whole list from local storage
     * @function deleteFromsearches
     * @param {string} keyword- Keyword to be removed
     * @returns {void}
     */
    delete: (keyword?: string): void => {
        try {
            if (keyword) {
                let searches = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '');
                if (searches && Array.isArray(searches)) {
                    searches = searches.filter(value => value !== keyword);
                } else {
                    searches = [];
                }
                localStorage.setItem(`${RECENT_SEARCHES_KEY}`, JSON.stringify(searches));
            } else {
                localStorage.setItem(`${RECENT_SEARCHES_KEY}`, JSON.stringify([]));
            }
        } catch (exception) {
            const searches: string[] = [];
            localStorage.setItem(`${RECENT_SEARCHES_KEY}`, JSON.stringify(searches));
        }
    },
};

export const shoppingUrl = {
    /**
     * Sets the shopping url into local storage
     * @function set
     * @param {string} url- Url to be stored
     * @returns {void}
     */
    set: (url: string): void => {
        try {
            if (url) {
                localStorage.setItem(`${SHOPPING_URL_KEY}`, JSON.stringify({ url }));
            }
        } catch (exception) {
            localStorage.setItem(`${SHOPPING_URL_KEY}`, JSON.stringify('/'));
        }
    },

    /**
     * Retrieves the shopping url from local storage
     * @function get
     * @returns {string}
     */
    get: (): string => {
        try {
            const url = JSON.parse(localStorage.getItem(SHOPPING_URL_KEY) || '');

            return url.url;
        } catch (exception) {
            return '/';
        }
    },
};
